import React, { useState } from 'react'
import { Mail, Linkedin, MapPin, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'

// Configuration - Replace these with your actual keys
const CONFIG = {
  // EmailJS Configuration (https://www.emailjs.com/)
  // 1. Create account at emailjs.com
  // 2. Add email service (Gmail, Outlook, etc.)
  // 3. Create email template with variables: {{from_name}}, {{from_email}}, {{subject}}, {{message}}
  // 4. Get your Service ID, Template ID, and Public Key
  EMAILJS_SERVICE_ID: 'YOUR_SERVICE_ID',
  EMAILJS_TEMPLATE_ID: 'YOUR_TEMPLATE_ID',
  EMAILJS_PUBLIC_KEY: 'YOUR_PUBLIC_KEY',
  
  // Google reCAPTCHA v2 Configuration (https://www.google.com/recaptcha/admin)
  // 1. Go to Google reCAPTCHA admin console
  // 2. Register a new site with reCAPTCHA v2 "I'm not a robot" checkbox
  // 3. Add your domains (localhost for testing, your production domain)
  // 4. Copy the Site Key below
  RECAPTCHA_SITE_KEY: 'YOUR_RECAPTCHA_SITE_KEY',
}

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recaptchaVerified, setRecaptchaVerified] = useState(false)
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const recaptchaContainerRef = React.useRef(null)
  const recaptchaWidgetId = React.useRef(null)
  
  // Load reCAPTCHA script and set up callbacks
  React.useEffect(() => {
    // Skip if not configured
    if (CONFIG.RECAPTCHA_SITE_KEY === 'YOUR_RECAPTCHA_SITE_KEY') {
      setRecaptchaVerified(true) // Allow submission without reCAPTCHA if not configured
      return
    }
    
    // Define callbacks on window object
    window.onRecaptchaVerify = (token) => {
      console.log('reCAPTCHA verified!')
      setRecaptchaVerified(true)
    }
    
    window.onRecaptchaExpired = () => {
      console.log('reCAPTCHA expired')
      setRecaptchaVerified(false)
    }
    
    window.onRecaptchaError = () => {
      console.log('reCAPTCHA error')
      setRecaptchaVerified(false)
    }
    
    window.onRecaptchaLoad = () => {
      console.log('reCAPTCHA script loaded')
      setRecaptchaLoaded(true)
    }
    
    // Load script if not already loaded
    if (!document.querySelector('script[src*="recaptcha"]')) {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    } else if (window.grecaptcha && window.grecaptcha.render) {
      // Script already loaded
      setRecaptchaLoaded(true)
    }
    
    return () => {
      delete window.onRecaptchaVerify
      delete window.onRecaptchaExpired
      delete window.onRecaptchaError
      delete window.onRecaptchaLoad
    }
  }, [])
  
  // Render reCAPTCHA widget when script is loaded
  React.useEffect(() => {
    if (recaptchaLoaded && recaptchaContainerRef.current && recaptchaWidgetId.current === null) {
      try {
        recaptchaWidgetId.current = window.grecaptcha.render(recaptchaContainerRef.current, {
          sitekey: CONFIG.RECAPTCHA_SITE_KEY,
          callback: 'onRecaptchaVerify',
          'expired-callback': 'onRecaptchaExpired',
          'error-callback': 'onRecaptchaError',
          theme: 'dark'
        })
        console.log('reCAPTCHA widget rendered')
      } catch (error) {
        console.error('reCAPTCHA render error:', error)
      }
    }
  }, [recaptchaLoaded])
  
  // Load EmailJS script
  React.useEffect(() => {
    if (document.querySelector('script[src*="emailjs"]')) return
    
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js'
    script.async = true
    document.head.appendChild(script)
    
    script.onload = () => {
      if (window.emailjs && CONFIG.EMAILJS_PUBLIC_KEY !== 'YOUR_PUBLIC_KEY') {
        window.emailjs.init(CONFIG.EMAILJS_PUBLIC_KEY)
      }
    }
  }, [])
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Check if configured
    if (CONFIG.EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID') {
      setStatus({
        type: 'error',
        message: 'Email service not configured. Please set up EmailJS credentials.'
      })
      return
    }
    
    // Check reCAPTCHA
    if (!recaptchaVerified && CONFIG.RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY') {
      setStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification.'
      })
      return
    }
    
    setIsSubmitting(true)
    setStatus({ type: '', message: '' })
    
    try {
      // Send email using EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: 'jmaheshrao@gmail.com',
      }
      
      await window.emailjs.send(
        CONFIG.EMAILJS_SERVICE_ID,
        CONFIG.EMAILJS_TEMPLATE_ID,
        templateParams
      )
      
      setStatus({
        type: 'success',
        message: 'Message sent successfully! I\'ll get back to you soon.'
      })
      setFormData({ name: '', email: '', subject: '', message: '' })
      
      // Reset reCAPTCHA
      if (window.grecaptcha && recaptchaWidgetId.current !== null) {
        window.grecaptcha.reset(recaptchaWidgetId.current)
        setRecaptchaVerified(false)
      }
      
    } catch (error) {
      console.error('Email send error:', error)
      setStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or email me directly.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }
  
  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: 'jmaheshrao@gmail.com',
      href: 'mailto:jmaheshrao@gmail.com',
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      value: 'linkedin.com/in/jmaheshrao',
      href: 'https://linkedin.com/in/jmaheshrao',
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'San Jose, CA',
    },
  ]
  
  return (
    <section id="contact" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Let's <span className="text-gradient">Connect</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            I'm always interested in discussing new opportunities, innovative projects, and ways to create impact through technology.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="glass rounded-2xl p-8">
              <h3 className="text-xl font-display font-semibold text-white mb-6">
                Get in Touch
              </h3>
              
              <div className="space-y-4">
                {contactMethods.map((method, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <method.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm">{method.label}</div>
                      {method.href ? (
                        <a 
                          href={method.href}
                          target={method.href.startsWith('http') ? '_blank' : undefined}
                          rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-white hover:text-primary transition-colors"
                        >
                          {method.value}
                        </a>
                      ) : (
                        <div className="text-white">{method.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="glass rounded-2xl p-8">
              <h3 className="text-xl font-display font-semibold text-white mb-4">
                What I'm Looking For
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <span className="text-gray-300">Senior AI/ML Engineering Leadership roles</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <span className="text-gray-300">CTO/VP Engineering opportunities</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <span className="text-gray-300">Technical advisory or consulting</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                  <span className="text-gray-300">Innovative startups with meaningful impact</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="glass rounded-2xl p-8">
            <h3 className="text-xl font-display font-semibold text-white mb-6">
              Send a Message
            </h3>
            
            {/* Status Messages */}
            {status.message && (
              <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                status.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                  : 'bg-red-500/10 border border-red-500/20 text-red-400'
              }`}>
                {status.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{status.message}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm text-gray-400 mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-50"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm text-gray-400 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-50"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-sm text-gray-400 mb-2">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 transition-colors disabled:opacity-50"
                  placeholder="How can I help?"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm text-gray-400 mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-primary/50 transition-colors resize-none disabled:opacity-50"
                  placeholder="Your message..."
                />
              </div>
              
              {/* reCAPTCHA */}
              {CONFIG.RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY' && (
                <div className="flex justify-center">
                  <div ref={recaptchaContainerRef} />
                </div>
              )}
              
              {/* Setup Instructions (only shown when not configured) */}
              {CONFIG.EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID' && (
                <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                  <p className="font-semibold mb-2">⚙️ Setup Required:</p>
                  <ol className="list-decimal list-inside space-y-1 text-yellow-400/80">
                    <li>Create an account at <a href="https://www.emailjs.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-300">emailjs.com</a></li>
                    <li>Set up an email service and template</li>
                    <li>Get your reCAPTCHA key at <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-300">Google reCAPTCHA</a></li>
                    <li>Update the CONFIG object in Contact.jsx</li>
                  </ol>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting || (CONFIG.RECAPTCHA_SITE_KEY !== 'YOUR_RECAPTCHA_SITE_KEY' && !recaptchaVerified)}
                className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact
